import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import { CTA_OUTLINE, CTA_SOLID, CtaStyles, ctaOverPrimary } from './_cta';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance, getContrastTextColor } from '../utils/luminance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';

// ============================================================================
// Office hours grouping (mobile drawer OFFICE block)
// ----------------------------------------------------------------------------
// Compact, self-contained copy of the Footer's office-hours grouping so the
// mobile drawer can render a "Mon - Fri / Saturday / Sunday" style schedule
// from the `office_hours` collection. Kept local to Header (rather than shared)
// to avoid coupling the two components; the logic is a handful of pure helpers.
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

// Strip a trailing country segment (", USA", ", United States", …) from the
// drawer's richtext address. Display-only — mirrors
// PropertyInfoPanel.stripTrailingCountry but operates on the resolved HTML so
// any trailing closing tags (e.g. </p>) are preserved. Only removes the
// segment when it sits at the very end (after it, only whitespace / closing
// tags), so real street/city/state/zip parts are never touched.
function stripTrailingCountryHtml(html: string): string {
  return html.replace(
    /,\s*(?:u\.?\s?s\.?\s?a\.?|u\.?\s?s\.?|united states(?: of america)?)\s*(?=(?:\s|<[^>]*>)*$)/i,
    '',
  );
}

// Break the drawer's richtext address onto two lines: street on the first
// line, "City, ST ZIP" on the second. Works on the resolved HTML (after
// field resolution + country stripping) by inserting a <br /> right after the
// FIRST comma that sits in text (not inside a tag) — for a standard
// "1701 Chandler Rd, Statesboro, GA 30458" that puts the street up top and the
// city/state/zip below. Display-only. If the address already carries an
// explicit line break (an editor-authored <br> or a multi-paragraph value),
// it's left untouched so we never fight a hand-formatted layout.
function breakAddressAfterStreet(html: string): string {
  if (/<br|<\/p\s*>\s*<p/i.test(html)) return html;
  let inTag = false;
  for (let i = 0; i < html.length; i++) {
    const ch = html[i];
    if (ch === '<') inTag = true;
    else if (ch === '>') inTag = false;
    else if (ch === ',' && !inTag) {
      // Drop the space that follows the comma so line two doesn't start indented.
      const rest = html.slice(i + 1).replace(/^\s+/, '');
      return `${html.slice(0, i + 1)}<br />${rest}`;
    }
  }
  return html;
}

function hasHourValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const n = val.trim().toLowerCase();
  return n !== '' && n !== 'false' && n !== 'null' && n !== 'undefined';
}

function formatHourTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const HOUR_DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const HOUR_DAY_SHORT: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};
const HOUR_DAY_FULL: Record<string, string> = {
  mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday',
  fri: 'Friday', sat: 'Saturday', sun: 'Sunday',
};

interface GroupedHour {
  label: string;
  hours: string;
}

function normalizeHourDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHour[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = HOUR_DAY_ORDER.indexOf(normalizeHourDay(a.day || ''));
    const bi = HOUR_DAY_ORDER.indexOf(normalizeHourDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHour[] = [];
  for (const entry of sorted) {
    if (!hasHourValue(entry.day)) continue;
    const key = normalizeHourDay(entry.day);
    const full = HOUR_DAY_FULL[key] || entry.day!;
    const short = HOUR_DAY_SHORT[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasHourValue(entry.open_time) && !hasHourValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatHourTime(entry.open_time || '')} - ${formatHourTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      // Extend the running range using short labels ("Mon - Fri").
      const dash = prev.label.indexOf(' - ');
      const start = dash !== -1 ? prev.label.slice(0, dash) : prev.label;
      prev.label = `${start} - ${short}`;
    } else {
      // A single day shows its full name; ranges collapse to short labels above.
      groups.push({ label: full, hours: hoursStr });
    }
  }
  return groups;
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/**
 * Aliases kept out of the main navigation regardless of their `visible` flag.
 * Only `home` — the centered logo already links to the home page, so a
 * redundant "Home" text link would just duplicate it.
 *
 * Every OTHER page is driven purely by its `visible` flag + `position` in
 * pages.json (see buildNavLinks): flip a page's visibility and it
 * appears/disappears here. Legal / utility / flow pages (privacy, sitemap,
 * thank-you, apply-now, …) are already `visible: false` in pages.json, so they
 * stay out without being hardcoded; set one `visible: true` and it will
 * intentionally show. This is the single source of truth for nav membership —
 * "what's set to show" — so there's no hidden curated list to get out of sync.
 */
const NAV_EXCLUDED_ALIASES = new Set(['home']);

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface PageSection {
  slot: string;
}

/**
 * Returns true when the page identified by `alias` (or, as a fallback, by
 * `pathname`) has `HeroSolidBG` as its primary hero — i.e. an interior page
 * where the navigation overlays the solid-primary hero rather than a
 * photographic hero. Looking up by alias works during SSG (the runtime
 * passes `page.alias` through `usePage()`) so the resulting class is baked
 * into the static HTML and the nav text is correct on first paint.
 */
function isOverSolidHero(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData as Record<string, PageEntry>;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return hero?.slot === 'HeroSolidBG';
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders the ApartmentSync (Repli360 /
 * RRAC) "interactive leasing view" widget, which is keyed by two per-
 * property custom fields: `custom_apartmentsync_javascript_id` (the
 * encoded token in the script URL) and `custom_apartmentsync_config_id`
 * (the site identifier). Both are declared as required-style tokens on
 * the apartment-sync-map block, and a property without both is a
 * half-configured / no-real-map state (Centre Point being the canonical
 * example — it has neither, but earlier versions of this check let it
 * leak into the nav when only one ID was populated as a placeholder).
 * If either is empty we drop the page from the nav (the SSG also marks
 * the page hidden + noindex so it isn't crawled — see ssg-entry.ts).
 */
function buildNavLinks(hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      // Interactive Map is the one data-gated page: the static build DROPS
      // this page entirely (no HTML emitted) when the property lacks a
      // complete ApartmentSync config — see ssg-entry.ts — so linking to it
      // would 404. We mirror that here. Every other visible page shows purely
      // on its `visible` flag; e.g. Virtual Tour is no longer gated on tour
      // data (the page always exists), so toggling `visible` in pages.json is
      // the single source of truth for it.
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { label: page.name, href: page.path });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Resident Login CTA inside the mobile drawer. Points at the property's
// resident portal (defaultField) with a `/resident-login` fallback, mirroring
// the Footer's Resident Login link so the two surfaces stay in sync.
const residentLoginLinkDef: SlotGroupDefinition = {
  label: 'Resident Login Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
      defaultValue: '/resident-login',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Social links surfaced at the bottom of the mobile drawer.
const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      // Richtext so editors can apply inline formatting (bold city, line
      // breaks, an embedded property field, etc.). `useSlotGroup` auto-resolves
      // richtext fields — the returned value is sanitized, field-resolved HTML.
      type: 'richtext',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap"
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* The parent label is still a real link so clicking it navigates to
          the parent page; the chevron + dropdown surface the child pages on
          hover/focus without stealing the primary affordance. */}
      <a
        href={link.href}
        className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
}: {
  link: NavLink;
  onNavigate: () => void;
  /** Retained in callers for parity with desktop but intentionally
      unused — see comment below. Children render flush with their
      parents in the drawer because the visual offset reads as misaligned
      noise in a vertical list (the desktop hover dropdown already
      conveys the hierarchy there). */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className="rise-nav-darken-hover block py-3 px-6 font-body text-xl font-normal uppercase tracking-[0.15em] text-[var(--vibe-color-text-on-primary)] transition-colors"
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  const overSolidHero = useMemo(
    () =>
      isOverSolidHero(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );

  // Is this the home page? The transparent (scrolled-fully-up) header renders
  // its text + logo in the `--vibe-color-text-on-primary` token ONLY on home
  // (whose photographic Hero is designed for that contrast pick). Every other
  // page's transparent nav overlays a `PageHero` — a photo under a fixed
  // `bg-black/40` scrim — where the token could resolve dark on a light primary
  // and wash out; those pages use plain white instead. Resolved by alias first
  // (works at SSG time so the class is baked into the static HTML) with a
  // pathname fallback for oddball preview/dev routes.
  const isHome = useMemo(() => {
    if (pageCtx?.alias) return pageCtx.alias === 'home';
    const path =
      typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path;
    return path === '/' || path === '';
  }, [pageCtx?.alias, pageCtx?.path]);

  // Pick the more readable text color for the transparent nav when it sits on
  // top of `HeroSolidBG` (which paints the section in `--vibe-color-primary`).
  // Uses the same `getContrastTextColor` helper the Footer uses (and that the
  // tokens runtime uses to compute `--vibe-color-text-on-primary`), so the
  // nav, the hero CTA, and the footer all pick the same contrast side on a
  // given primary.
  //
  // The earlier implementation here did `pickHighestContrast(primary,
  // [textOnPrimary, textOnSecondary])`, which was technically WCAG-optimal
  // but produced black text on medium-tone primaries (gold, tan, dusty
  // blue): on a primary with luminance ~0.3, dark-on-medium scores ~5:1 vs.
  // white-on-medium ~3:1, so the picker chose dark — disagreeing with the
  // runtime's own text-on-primary token and rendering muddy black nav links
  // on what should be a white-text design. The shared helper applies the
  // documented 0.55 threshold that favors white on those medium tones.
  const tokens = useTokens();
  const navOverHeroColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);


  // Interactive Map — drops out of the nav when the property has no
  // ApartmentSync (Repli360 / RRAC) IDs to drive the "interactive
  // leasing view" widget on that page. We require BOTH the JavaScript
  // ID (script URL token) and the Config ID (site identifier) — a
  // property with only one populated is a half-configured state and
  // shouldn't show the page (e.g. Centre Point, which has neither, was
  // leaking into the nav when only the JavaScript ID was checked).
  // Mirrors the SSG-side hide + noindex logic in ssg-entry.ts so
  // navigation, sitemap, and crawler hints stay consistent. The fields
  // are custom property fields (typed as `text`) so we read them via
  // `useProperty()` directly rather than the strongly-typed
  // `usePropertyField` (which only knows the built-in scalar fields).
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = propertyForLeasing?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode — e.g.
  // `custom_engrain_sightmap_url`) or given a literal string (Custom mode).
  // In both cases useContentToken returns the already-RESOLVED value (the
  // field's value, or the literal), so we simply expose the Interactive Map
  // page whenever that resolved value is non-empty. Blank by default (no
  // override). ADDITIVE to the ApartmentSync detection above, so existing
  // ApartmentSync-configured properties keep working untouched.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasInteractiveLeasing),
    [hasInteractiveLeasing],
  );

  // Split the page links into two roughly-even groups so they can flank the
  // centered logo on desktop (first half to the left, second half to the
  // right). The extra link on an odd count goes to the left group.
  const [leftLinks, rightLinks] = useMemo(() => {
    const mid = Math.ceil(navLinks.length / 2);
    return [navLinks.slice(0, mid), navLinks.slice(mid)] as const;
  }, [navLinks]);

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // Logo color choice mirrors the nav text color logic in every state:
  //   • Scrolled (solid --vibe-color-primary bar) → match the picked
  //     on-primary token: light on-color → mono_white, dark on-color →
  //     mono_black, so the logo reads against the primary-color header bar.
  //     Falls back to whatever the editor picked (logoRaw.image, typically
  //     the primary full-color logo) if the matching mono logo is missing.
  //   • Transparent over HeroSolidBG (interior page) → match the picked
  //     on-color token: light text → mono_white, dark text → mono_black.
  //   • Transparent over photographic Hero (home) → also match the picked
  //     on-primary token, so the logo tracks the nav text (which now renders
  //     in --vibe-color-text-on-primary rather than forced white).
  // The logo mono variant tracks whatever color the header text is using in the
  // current state:
  //   • Scrolled (solid --vibe-color-primary bar) → follow the on-primary
  //     contrast pick (dark on-primary → mono_black, light → mono_white).
  //   • Transparent + home → follow the same on-primary pick, matching the nav
  //     text which renders in --vibe-color-text-on-primary here.
  //   • Transparent + any other page → the nav text is plain white over the
  //     dark PageHero scrim, so use the white logo regardless of the primary.
  //   • Mobile drawer open → the drawer paints the full screen in
  //     --vibe-color-primary, so the logo (which sits in the header bar over
  //     that green) follows the same on-primary contrast pick as the drawer
  //     text: white logo on a dark primary, black logo on a light one.
  const onPrimaryWantsDark =
    !!navOverHeroColor && relativeLuminance(navOverHeroColor) < 0.5;
  const wantsDarkLogo = (scrolled || isHome || menuOpen) ? onPrimaryWantsDark : false;
  const logoImage = wantsDarkLogo
    ? (monoBlackLogo || logoRaw.image)
    : (monoWhiteLogo || logoRaw.image);
  // Keep the SlotGroupResult (Record<string, string | null>) shape so both
  // `logo.image` and `logo.alt` stay typed — a bare object spread would drop
  // the index signature and lose `.alt`.
  const logo: typeof logoRaw = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentLoginLink = useSlotGroup('header.resident_login_link', residentLoginLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const facebookLink = useSlotGroup('header.facebook_link', facebookLinkDef);
  const instagramLink = useSlotGroup('header.instagram_link', instagramLinkDef);

  // Office hours for the drawer's OFFICE block, grouped into day ranges.
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedOfficeHours =
    officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Google Fonts — matches the Verso theme demo: Trirong (serif headings) +
  // Barlow (sans body). Both are free Google Fonts, no licensing required.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="verso"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Trirong:wght@400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'verso');
    document.head.appendChild(link);
  }, []);

  // Measure header height for spacer
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  // Hamburger / close-icon bar color. The toggle button lives in the header
  // bar (the drawer panel starts *below* it at `top: headerHeight`), so the
  // icon sits over the same surface whether the menu is open or closed — the
  // hero when transparent, the primary bar when scrolled. It therefore mirrors
  // the nav-link color logic in the stylesheet below rather than the drawer
  // background:
  //   • Scrolled (solid primary-color bar) → the luminance-picked on-primary
  //     color (same token the nav links use).
  //   • Transparent over a solid-primary hero (interior pages like Floor
  //     Plans / Resident Access) → the same picked on-hero color the nav
  //     links flip to, so the icon is light over a dark hero instead of the
  //     dark `--vibe-color-text`.
  //   • Transparent over a photographic hero (home) → the text-on-primary
  //     token, matching the nav links' treatment over the hero (which paints
  //     in --vibe-color-primary). Falls back to white before tokens hydrate.
  // (An earlier CSS rule tried to do this via a `bg-[var(--vibe-color-text)]`
  // attribute selector, but the icon spans set their color inline and never
  // carried that class, so the rule never matched — hence computing it here.)
  const hamburgerColor = scrolled
    ? (navOverHeroColor || 'var(--vibe-color-text)')
    : overSolidHero
      ? (navOverHeroColor || '#fff')
      : isHome
        ? 'var(--vibe-color-text-on-primary, #fff)'
        : '#fff';

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out${scrolled ? ' header-scrolled' : ' bg-transparent header-transparent'}${overSolidHero ? ' header-over-solid-hero' : ''}${isHome ? ' header-home' : ''}`}
        style={{
          top: 0,
          transition: 'background-color 0.5s ease',
          // The open mobile drawer paints the whole screen in the primary
          // color and sits *behind* this bar, so paint the bar the same green
          // while it's open — otherwise a transparent (scrolled-up) header
          // would leave a mismatched strip above the drawer.
          backgroundColor: (scrolled || menuOpen) ? 'var(--vibe-color-primary)' : undefined,
          // Luminance-picked text color for the transparent nav over a
          // `HeroSolidBG` (primary-color) section. Falls back to white via the
          // CSS rules below when this var is absent (e.g. home page with the
          // photographic hero, or before tokens have hydrated).
          ...(navOverHeroColor ? { ['--rise-nav-over-hero-text' as string]: navOverHeroColor } : {}),
          // Transparent (scrolled-fully-up) nav text color. Only the home page
          // gets the on-primary contrast pick here; every other page leaves
          // this unset so the CSS falls back to white over its dark PageHero.
          ...(isHome && navOverHeroColor
            ? { ['--rise-nav-transparent-text' as string]: navOverHeroColor }
            : {}),
        }}
      >
        {/* Editor-only control. Holds the name of the property field whose
            presence exposes the Interactive Map page in the nav (consumed by
            `hasInteractiveMapOverride` above — e.g. `custom_engrain_sightmap_url`).
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16 lg:h-[72px]">
              {/* Left navigation (desktop) — first half of the page links.
                  Right-aligned (justify-end) so the group hugs the logo: this
                  keeps the gap on the logo's left equal to the gap on its right
                  even when the two groups hold a different number of links
                  (odd counts put the extra link here), which a justify-center
                  split would leave visibly lopsided. Hidden on mobile, where
                  the links live in the drawer. */}
              <nav
                className="hidden lg:flex items-center justify-end gap-3 xl:gap-5 flex-1 min-w-0"
                role="navigation"
                aria-label="Main navigation"
              >
                {leftLinks.map((link) => (
                  <DesktopNavLink key={link.href} link={link} />
                ))}
              </nav>

              {/* Logo — sits in normal flow between the two nav groups, so it
                  always occupies real space and the side groups can never run
                  under it. The flanking flex-1 nav groups keep it centered on
                  desktop. On mobile it's at the start of the row with the
                  hamburger pushed to the right. */}
              {/* The EditableSlotGroup wrapper (a plain <div> off the editor)
                  is the actual flex sibling of the two nav groups, so the
                  spacing + `flex-shrink-0` MUST live here, not on the inner
                  <a>. Without flex-shrink-0 on this box the flex-1 nav groups
                  can collapse it and the links overlap the logo; the mx keeps
                  an even gutter between the logo and the innermost link on
                  each side. */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="flex-shrink-0 lg:mx-6 xl:mx-10"
              >
                <a href="/" className="block">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Rise
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Right navigation (desktop) — second half of the page links.
                  Left-aligned (justify-start) so it hugs the logo, mirroring
                  the left group for symmetric spacing around the centered
                  logo. */}
              <nav
                className="hidden lg:flex items-center justify-start gap-3 xl:gap-5 flex-1 min-w-0"
                role="navigation"
                aria-label="Main navigation (continued)"
              >
                {rightLinks.map((link) => (
                  <DesktopNavLink key={link.href} link={link} />
                ))}
              </nav>

              {/* Mobile menu toggle — pinned to the right edge on mobile,
                  hidden on desktop where the split nav is shown instead.
                  Closed: a bare hamburger over the hero / bar. Open: an
                  accent (secondary-color) box in the corner with a small
                  "MENU" caption above the same hamburger, matching the drawer
                  treatment. */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className={`lg:hidden relative z-[60] flex flex-col items-center justify-center ml-auto transition-all duration-300 ${
                  menuOpen ? 'w-16 h-16 gap-1.5 -mr-4 sm:-mr-6' : 'w-10 h-10'
                }`}
                style={
                  menuOpen
                    ? {
                        // Light "sage" tint of the primary color — the same fill
                        // the drawer's CTA pills use — so the close box pops off
                        // the primary-colored drawer instead of blending in (the
                        // property `secondary` color is often a near-identical
                        // tone to `primary`, which made the old secondary-filled
                        // box and its X nearly invisible).
                        backgroundColor:
                          'color-mix(in srgb, var(--vibe-color-primary) 28%, #ffffff)',
                      }
                    : undefined
                }
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                {/* "CLOSE" caption — only visible while open, in the primary
                    color so it reads clearly on the light box. */}
                <span
                  className={`font-body text-[10px] font-semibold tracking-[0.2em] uppercase leading-none transition-opacity duration-300 ${
                    menuOpen ? 'opacity-100' : 'sr-only'
                  }`}
                  style={{ color: 'var(--vibe-color-primary)' }}
                  aria-hidden={!menuOpen}
                >
                  {menuOpen ? 'Close' : 'Menu'}
                </span>
                {/* Bars — morph into a close (X) icon when the menu is open so
                    there's always a clear way to dismiss the drawer: the top
                    and bottom bars rotate onto the center line to form an X and
                    the middle bar fades out. Open, they're drawn in the primary
                    color for contrast against the light box; closed, they sit as
                    a three-line hamburger in the header-bar color. */}
                <span className="relative block w-6 h-4">
                  <span
                    style={{ backgroundColor: menuOpen ? 'var(--vibe-color-primary)' : hamburgerColor }}
                    className={`absolute left-0 top-1/2 block w-6 h-0.5 -mt-px transition-all duration-300 ${menuOpen ? 'translate-y-0 rotate-45' : '-translate-y-1.5'}`}
                  />
                  <span
                    style={{ backgroundColor: menuOpen ? 'var(--vibe-color-primary)' : hamburgerColor }}
                    className={`absolute left-0 top-1/2 block w-6 h-0.5 -mt-px transition-all duration-300 ${menuOpen ? 'opacity-0' : 'opacity-100'}`}
                  />
                  <span
                    style={{ backgroundColor: menuOpen ? 'var(--vibe-color-primary)' : hamburgerColor }}
                    className={`absolute left-0 top-1/2 block w-6 h-0.5 -mt-px transition-all duration-300 ${menuOpen ? 'translate-y-0 -rotate-45' : 'translate-y-1.5'}`}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        {/* Full-screen drawer painted in the primary brand color. It sits
            ABOVE the sticky sub-nav (z-40) but BELOW the header bar (z-50) so
            the logo + the accent MENU/X toggle stay visible on top; a spacer
            the height of the header keeps the first nav link clear of that bar.
            The panel slides in from the right (translate-x-full → translate-x-0)
            and fades, so opening it reads as a right-hand drawer rather than a
            plain cross-fade. `transition-all` animates both the transform and
            the opacity; `invisible` (kept only while closed) makes the fully
            slid-out panel unreachable to AT / tab order. */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-0 overflow-y-auto transition-all duration-300 ease-in-out z-[45] ${
            menuOpen
              ? 'opacity-100 visible translate-x-0'
              : 'opacity-0 invisible pointer-events-none translate-x-full'
          }`}
          style={{ backgroundColor: 'var(--vibe-color-primary)' }}
        >
          {/* Spacer clearing the fixed header bar. */}
          <div style={{ height: `${headerHeight}px` }} aria-hidden="true" />

          {/* Logo at the top of the drawer. Reuses the same `header.logo`
              slot group as the header bar (already editable there) and the
              same mono-variant pick — `logo.image` already accounts for the
              open-drawer state (menuOpen) so it contrasts against the
              primary-color panel. Rendered as a plain, centered link here so
              the editor keeps a single logo control (the header-bar wrapper);
              tapping it navigates home and closes the drawer. */}
          <div className="px-6 pt-2 pb-6 flex justify-center">
            <a href="/" onClick={closeMobileMenu} className="block" aria-label="Home">
              {logo.image ? (
                <VibesitesImage
                  url={logo.image}
                  alt={logo.alt || 'Community Logo'}
                  className="h-12 w-auto object-contain"
                  sizes="200px"
                />
              ) : (
                <div
                  className="font-heading text-2xl font-bold"
                  style={{ color: 'var(--vibe-color-text-on-primary, #fff)' }}
                >
                  The Rise
                </div>
              )}
            </a>
          </div>

          {/* Navigation links */}
          <nav role="navigation" aria-label="Mobile navigation" className="pt-6 pb-2">
            {navLinks.map((link) => (
              <React.Fragment key={link.href}>
                <MobileNavLink link={link} onNavigate={closeMobileMenu} />
                {link.children?.map((child) => (
                  <MobileNavLink
                    key={child.href}
                    link={child}
                    onNavigate={closeMobileMenu}
                    isChild
                  />
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* Primary CTAs — Resident Login (solid) + Apply Now (outline),
              keyed to the SAME canonical CTA styles the Footer uses over the
              primary surface: `ctaOverPrimary` inverts the accent to the
              on-primary token so the buttons read against the primary-colored
              drawer. Wrapped in EditableSlotGroup so the editor can retarget /
              relabel each from inside the open drawer. */}
          <div className="px-6 pt-4 flex flex-col gap-3" style={ctaOverPrimary}>
            <CtaStyles />
            {/* Mirror the Footer's Resident Login hover: on hover the solid
                button fills with the resolved secondary hover fill
                (`--cta-hover-fill`, published by <CtaStyles/> and auto-darkened
                when a white label wouldn't clear WCAG AA on the raw secondary)
                and keeps a white label, with the shared ::before darkening
                overlay suppressed so the fill shows cleanly. */}
            <style>{`
              .header-resident-login-cta.v-cta--solid:hover {
                color: #ffffff;
                background: var(--cta-hover-fill, var(--vibe-color-secondary));
                border-color: var(--cta-hover-fill, var(--vibe-color-secondary));
              }
              .header-resident-login-cta.v-cta--solid:hover::before {
                opacity: 0;
              }
            `}</style>
            <EditableSlotGroup groupPath="header.resident_login_link" definition={residentLoginLinkDef} className="w-full">
              <a
                href={residentLoginLink.url || '/resident-login'}
                target={residentLoginLink._target || '_blank'}
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className={`w-full header-resident-login-cta ${CTA_SOLID}`}
              >
                {residentLoginLink.label || 'Resident Login'}
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef} className="w-full">
              <a
                href={applyNowLink.url || '/apply-now'}
                target={applyNowLink._target || '_self'}
                onClick={closeMobileMenu}
                className={`w-full ${CTA_OUTLINE}`}
              >
                {applyNowLink.label || 'Apply Now'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* OFFICE — label, address, and grouped office hours. */}
          <div className="px-6 pt-8 pb-4">
            <RichTextContent
              path="header.office_label"
              defaultValue="<p>Office</p>"
              as="h3"
              className="font-body text-sm font-semibold tracking-[0.2em] uppercase mb-3 mobile-drawer-heading"
            />

            <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef} className="block">
              <a
                href={addressLink.url || propertyAddressUrl || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="mobile-drawer-muted block font-body text-base tracking-[0.1em] leading-relaxed hover:opacity-80 transition-opacity"
              >
                {/* addressLink.address is richtext — useSlotGroup already
                    resolved + sanitized it (embedded field tokens replaced),
                    so we inject the HTML directly. The trailing country
                    segment (", USA") is stripped for display only. */}
                <span dangerouslySetInnerHTML={{ __html: breakAddressAfterStreet(stripTrailingCountryHtml(addressLink.address || '')) }} />
              </a>
            </EditableSlotGroup>

            {groupedOfficeHours.length > 0 && (
              <dl className="mt-4 space-y-1.5">
                {groupedOfficeHours.map((group, i) => (
                  <div
                    key={i}
                    className="mobile-drawer-muted flex items-baseline justify-between gap-6 font-body text-base tracking-[0.05em]"
                  >
                    <dt>{group.label}</dt>
                    <dd className="text-right">{group.hours}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          {/* Social — Facebook / Instagram, rendered as light "sage" circles
              with the primary color as the icon (mirrors the Footer badges).
              Each only appears when its URL is set. */}
          {(facebookLink.url || instagramLink.url) && (
            <div className="px-6 pb-10 pt-2 flex items-center gap-3">
              {facebookLink.url && (
                <EditableSlotGroup groupPath="header.facebook_link" definition={facebookLinkDef}>
                  <a
                    href={facebookLink.url}
                    target={facebookLink._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={facebookLink.label || 'Facebook'}
                    className="mobile-drawer-social inline-flex items-center justify-center w-11 h-11 rounded-full transition-opacity hover:opacity-90"
                  >
                    <FontAwesomeIcon icon={faFacebook} className="w-5 h-5 text-[var(--vibe-color-primary)]" />
                  </a>
                </EditableSlotGroup>
              )}
              {instagramLink.url && (
                <EditableSlotGroup groupPath="header.instagram_link" definition={instagramLinkDef}>
                  <a
                    href={instagramLink.url}
                    target={instagramLink._target || '_blank'}
                    rel="noopener noreferrer"
                    aria-label={instagramLink.label || 'Instagram'}
                    className="mobile-drawer-social inline-flex items-center justify-center w-11 h-11 rounded-full transition-opacity hover:opacity-90"
                  >
                    <FontAwesomeIcon icon={faInstagram} className="w-5 h-5 text-[var(--vibe-color-primary)]" />
                  </a>
                </EditableSlotGroup>
              )}
            </div>
          )}
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Page background                                                 */
        /* ================================================================ */
        /* Paint the page/layout canvas with the base background token so the
           area behind every page matches sections that use
           --vibe-color-background (e.g. PropertyOverview) instead of falling
           back to a host default. */
        body,
        .vibesites-layout,
        .vibesites-main {
          background-color: var(--vibe-color-background);
        }

        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        /* Scrolled header: solid primary-color bar. Expressed as a CSS rule
           (not only the inline style on <header>) so the background is applied
           the same proven way the Hero section paints its primary background,
           independent of how custom-property inline styles are handled. */
        .header-scrolled {
          background-color: var(--vibe-color-primary);
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-primary);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header (scrolled fully up): nav links → the
           --rise-nav-transparent-text color (header bar only — NOT inside the
           mobile menu panel). Only the home page sets that variable (to the
           on-primary contrast pick, matching its photographic hero); every
           other page leaves it unset, so its transparent nav falls back to
           plain white over the dark PageHero scrim. */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: var(--rise-nav-transparent-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: var(--rise-nav-transparent-text, #fff) !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* Scrolled header: the solid bar is now painted in --vibe-color-primary
           (see inline backgroundColor), so the dark theme text would vanish.
           Swap nav links + utility buttons to the same luminance-picked
           on-primary color the transparent-over-primary nav uses. */
        .header-scrolled .header-bar a.font-body,
        .header-scrolled .header-bar nav a,
        .header-scrolled .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-scrolled .header-bar nav a:hover,
        .header-scrolled .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .rise-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a,
        .header-scrolled .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover,
        .header-scrolled .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Mobile menu panel is painted in --vibe-color-primary, so its links
           render in the on-primary token regardless of the header bar's
           transparent / scrolled state above it. The hover affordance for
           nav links is the animated underline from .rise-nav-darken-hover
           below, so we hold text color steady on hover here too. The two
           full-width CTA buttons (.v-cta) manage their own at-rest / hover
           colors via the canonical CTA styles, so they're excluded from this
           blanket rule. */
        .mobile-menu-panel a:not(.v-cta),
        .mobile-menu-panel button {
          color: var(--vibe-color-text-on-primary, #fff);
        }
        .mobile-menu-panel a:not(.v-cta):hover {
          color: var(--vibe-color-text-on-primary, #fff);
        }

        /* OFFICE heading + muted rows: light "sage" tints derived from the
           on-primary color so they read as a softened white on the green
           drawer without hard-coding a specific brand tone. */
        .mobile-menu-panel .mobile-drawer-heading {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 65%, var(--vibe-color-primary));
        }
        .mobile-menu-panel .mobile-drawer-muted,
        .mobile-menu-panel a.mobile-drawer-muted {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 78%, transparent);
        }

        /* Social badges: a light tint of the primary color, primary-color icon. */
        .mobile-menu-panel .mobile-drawer-social {
          background-color: color-mix(in srgb, var(--vibe-color-primary) 28%, #ffffff);
        }

        /* Nav links: animated underline on hover (used by Header
           DesktopNavLink, "More" dropdown items, MobileNavLink, and the
           Navigation sub-bar). All of these contexts render against a
           white surface, so the affordance is an underline rather than a
           color shift — the underline is drawn via text-decoration so it
           rides the text baseline and works at any link padding. The
           transparent-header (over-hero) brightening hover above keeps
           its own color shift via earlier !important rules; the
           underline simply layers on top with currentColor. */
        .rise-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Address / phone rows in the mobile drawer wear the
           rise-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .rise-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .rise-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Transparent header: hamburger bars + site-name fallback follow the
           same --rise-nav-transparent-text color as the nav links (home →
           on-primary pick; other pages → white fallback). The hamburger span
           color is also set inline in JS; this rule is the CSS backstop. */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-transparent-text, #fff) !important;
        }

        /* Transparent header: site name fallback text → nav transparent color */
        .header-transparent .font-heading {
          color: var(--rise-nav-transparent-text, #fff) !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.office_label': {
      label: 'Office Label',
      type: 'richtext',
      defaultValue: '<p>Office</p>',
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Expose the Interactive Map page in the navigation if this token or field contains a value (e.g. custom_engrain_sightmap_url). Leave blank to hide.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_login_link': {
      label: 'Resident Login Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_resident_portal_url',
          suggestedFields: ['property_resident_portal_url'],
          defaultValue: '/resident-login',
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Login' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          // Richtext for inline formatting; useSlotGroup auto-resolves the
          // field-mapped/authored HTML before it reaches the component.
          type: 'richtext',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.facebook_link': {
      label: 'Facebook',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'property_facebook_url', suggestedFields: ['property_facebook_url'] },
        label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.instagram_link': {
      label: 'Instagram',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'property_instagram_url', suggestedFields: ['property_instagram_url'] },
        label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
